import { Nullable } from '../types';
import { fetchChatResponse } from './api';
import {
  getPrimaryMessageWrapper,
  recieveAndAppendMessage,
  sendAndAppendMessage,
  showDots,
} from './message-helpers';
import { templateTextWidget } from './template';
import { createToken } from './token';
import { MainWindow, TextChatUIElements } from './types';
import { sanitizeURL } from '../../shared/utils/sanitize';

const widget = document.createElement('template');
widget.innerHTML = templateTextWidget;

class WitzoChatElement extends HTMLElement {
  /**Url to connect to the webhook */
  url: Nullable<string> = null;
  /**Reference for elements */
  chat: Nullable<TextChatUIElements> = null;

  default_primary_text: Nullable<string> = null;

  bot_logo_color: Nullable<string> = ``;

  send_button_color: Nullable<string> = ``;
  
  unique_id:Nullable<string>=``;

  /**Is Initialized */
  isInitialized: boolean = false;
  private _mainWindow: MainWindow;

  private succesfulChatCount: number;
  private date: Date;

  constructor() {
    super();
    const node = widget.content.cloneNode(true);
    //Assume no outside influence on the styling should be possible
    const root = this.attachShadow({ mode: 'closed' });
    root.appendChild(node);

    this._mainWindow = {
      back: () => {},
      close: () => {},
    };

    this.getElements(root);
    this.succesfulChatCount = 0;
    this.date = new Date();

    const date = sessionStorage.getItem('witzo_chat_date');
    if (date) {
      this.date = new Date(date);
    } else {
      sessionStorage.setItem('witzo_chat_date', new Date().toISOString());
    }

    const chatCount = sessionStorage.getItem('witzo_chat_count');
    if (chatCount) this.succesfulChatCount = Number(chatCount);
    else sessionStorage.setItem('witzo_chat_count', `0`);

    // Initialize as null instead of empty string
    this.default_primary_text = null;
    this.bot_logo_color = ``;
    this.send_button_color = '';
  }

  /**
   * @description retrieve elements from shadow dom and ensures initialization
   */
  private getElements(root: ShadowRoot) {
    const query = <T extends HTMLElement>(selctor: string) => root.querySelector(selctor) as T;
    try {
      this.chat = {
        backButton: query('#backToSelection'),
        chatWindow: query('#textChatWidget'),
        closeButton: query('#closeTextChat'),
        sendButton: query('#textSendButton'),
        messageInput: query('#textMessageInput'),
        messagesContainer: query('#textMessagesArea'),
        sendButtonRect: root.querySelector('#send-button-color') as SVGRectElement,
      };
      this.isInitialized = true;
    } catch (err) {
      this.isInitialized = false;
      console.warn(`Couldn't initialize ${err}`);
    }
  }

  /**
   * @description Parse markdown text to HTML
   */
  private parseMarkdownToHTML(text: string): string {
    if (!text || text.trim() === '') {
      console.warn('Empty text passed to parseMarkdownToHTML');
      return '';
    }

    // Normalize newlines & collapse excessive blank lines
    const lines = text
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .split('\n');

    const out: string[] = [];
    let inUL = false;
    let inOL = false;

    const flushLists = () => {
      if (inUL) {
        out.push('</ul>');
        inUL = false;
      }
      if (inOL) {
        out.push('</ol>');
        inOL = false;
      }
    };

    const formatInline = (s: string) => {
      // links [text](url) with URL sanitization
      s = s.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        (_match, linkText, url) => {
          const safeUrl = sanitizeURL(url);
          return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
        }
      );
      // **bold**
      s = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return s;
    };

    for (let raw of lines) {
      const line = raw.trim();

      // Blank line -> close any open list and add a small spacer
      if (line === '') {
        flushLists();
        continue;
      }

      // Numbered list "1. item"
      const olMatch = line.match(/^(\d+)\.\s+(.*)$/);
      if (olMatch) {
        const item = formatInline(olMatch[2]);
        if (inUL) {
          out.push('</ul>');
          inUL = false;
        }
        if (!inOL) {
          out.push('<ol>');
          inOL = true;
        }
        out.push(`<li>${item}</li>`);
        continue;
      }

      // Bulleted list "- item"
      const ulMatch = line.match(/^-\s+(.*)$/);
      if (ulMatch) {
        const item = formatInline(ulMatch[1]);
        if (inOL) {
          out.push('</ol>');
          inOL = false;
        }
        if (!inUL) {
          out.push('<ul>');
          inUL = true;
        }
        out.push(`<li>${item}</li>`);
        continue;
      }

      // Standalone label lines ending with ":" (e.g., "Note:", "Tip:", "**Note:**")
      // Ensures EXACTLY one <br> after it — no extra bottom space.
      const labelMatch = line.match(/^\**\s*([^*].*?):\s*\**$/);
      if (labelMatch) {
        flushLists();
        // preserve any inline **bold** within the label too
        const labelText = formatInline(labelMatch[1]);
        out.push(`<strong>${labelText}:</strong><br>`);
        continue;
      }

      // Normal paragraph
      flushLists();
      out.push(`<p>${formatInline(line)}</p>`);
    }

    flushLists();

    // Final tidy-up: remove accidental double breaks or empty paragraphs
    return out
      .join('')
      .replace(/(<br>\s*){2,}/g, '<br>')
      .replace(/<p>\s*<\/p>/g, '');
  }

  /**
   * @description Process and display the default primary message
   */
  private processAndDisplayMessage() {
    if (!this.chat?.messagesContainer) {
      console.warn('Messages container not available');
      return;
    }

    if (!this.default_primary_text || this.default_primary_text.trim() === '') {
      console.warn('No default primary text to process');
      return;
    }

    const wrapper = getPrimaryMessageWrapper(
      this.chat.messagesContainer,
      this.bot_logo_color ?? '#fc0e3f'
    );

    const formattedContent = this.parseMarkdownToHTML(this.default_primary_text);
    const htmlWrapped = `<div class="md-content">${formattedContent}</div>`;

    const getText = async () => ({
      content: htmlWrapped,
      isHtml: true, // make sure your renderer uses innerHTML when this is true
    });
    recieveAndAppendMessage(wrapper, this.chat!.messagesContainer, getText());
  }

  /**
   * @description external function to set the url to connect to web-hook
   */
  set chatUrl(url: string) {
    this.url = url;
  }

  set mainWindow(_mainWindow: MainWindow) {
    this._mainWindow = _mainWindow;
  }

  set defaultPrimaryText(text: string) {
    this.default_primary_text = text;

    // If elements aren't ready yet, the text will be processed in connectedCallback
    if (!this.isInitialized || !this.chat?.messagesContainer) {
      return;
    }

    this.processAndDisplayMessage();
  }

  set botBackgroundColor(color: string) {
    this.bot_logo_color = color;
  }

  set sendBackgroundColor(color: string) {
    this.send_button_color = color;
    if (this.chat) this.chat.sendButtonRect.style.fill = color;
  }

  set uniqueId(id:string){
    this.unique_id=id;
  }

  async handleResponse(text: string) {
    if (!text || !this.url) return;
    if (!this.chat) return;

    const sessionId = createToken();

    sendAndAppendMessage(this.chat.messagesContainer, this.chat.messageInput, text);
    const response = await fetchChatResponse(this.url, sessionId, text, this.succesfulChatCount,this.unique_id ?? '');
  }

  connectedCallback() {
    this.bindEvents();

    // Process any pending default text after connection and initialization
    if (this.isInitialized && this.default_primary_text) {
      this.processAndDisplayMessage();
    }
  }

  bindEvents() {
    if (!this.chat || this.isInitialized === false) return;
    const { backButton, closeButton, sendButton, messageInput, messagesContainer, chatWindow } =
      this.chat;

    backButton.addEventListener('click', () => {
      this._mainWindow.back();
      this.chat?.chatWindow.classList.add('hidden');
    });

    closeButton.addEventListener('click', () => {
      this._mainWindow.close();
    });

    const gapRequired = 2 * 60 * 1000;
    sendButton.addEventListener('click', () => {
      if (!this.url) return;
      const text = this.chat!.messageInput.value.trim();

      const gap = new Date().getTime() - this.date.getTime();
      if (gap > gapRequired) {
        this.succesfulChatCount = 0;
        this.date = new Date();
      }

      if (text !== '') {
        sendAndAppendMessage(messagesContainer, messageInput, text);

        const wrapper = showDots(messagesContainer, this.bot_logo_color ?? '#fc0e3f');
        const sessionId = createToken();
        const response = fetchChatResponse(this.url, sessionId, text, this.succesfulChatCount,this.unique_id ?? '');

        this.succesfulChatCount += 1;
        sessionStorage.setItem('witzo_chat_count', `${this.succesfulChatCount}`);

        Promise.all([recieveAndAppendMessage(wrapper, messagesContainer, response)]);
      }
    });

    messageInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendButton.click();
      }
    });
  }

  /**Minimises the chat window */
  minimizeWidget() {
    if (!this.chat) return;

    this.chat.chatWindow.classList.add('minimizing');
    setTimeout(() => {
      this.chat?.chatWindow.classList.add('hidden');
      this.chat?.chatWindow.classList.remove('minimizing');
      //this.chat?.openButton.classList.remove('hidden');
    }, 300);
  }

  /**Maximize the chat window */
  maximizeWidget() {
    if (!this.chat) return;

    //this.chat.openButton.classList.add('hidden');
    this.chat.chatWindow.classList.remove('hidden');
    this.chat.messageInput.focus();
  }
}

export { WitzoChatElement };

